import * as React from "react";
import { Severity } from "@velaro/velaro-component-lib";
import LoadingSpinner from "@/components/LoadingSpinner";
import useApi from "@/hooks/useApi";
import useToastAlert from "@/hooks/useToastAlert";
import SyncPlanTable, { SiteSyncResult } from "./SyncPlanTable";

interface SiteOption {
  siteId: number;
  companyName: string;
}

type ConflictMode = "skip" | "overwrite" | "rename";

interface IncludeOptions {
  workflows: boolean;
  bots: boolean;
  siteSettings: boolean;
  deployments: boolean;
}

interface BlobEntry {
  blobKey: string;
  label: string;
  sourceSiteId: number;
  pushedBy: string | null;
  exportedAt: string;
  sizeBytes: number;
  bundleVersion: string;
}

// ── Tab helpers ────────────────────────────────────────────────────────────────

function TabButton({
  active,
  onClick,
  children
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
        active
          ? "border-blue-600 text-blue-700"
          : "border-transparent text-gray-500 hover:text-gray-700"
      }`}
    >
      {children}
    </button>
  );
}

// ── Site selector ──────────────────────────────────────────────────────────────

function SiteSelect({
  sites,
  value,
  exclude,
  onChange,
  label,
  id
}: {
  sites: SiteOption[];
  value: number | null;
  exclude: number | null;
  onChange: (id: number | null) => void;
  label: string;
  id: string;
}) {
  const [search, setSearch] = React.useState("");
  const filtered = sites.filter(
    s => s.siteId !== exclude && (
      s.companyName.toLowerCase().includes(search.toLowerCase()) ||
      String(s.siteId).includes(search)
    )
  );

  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">
        {label}
      </label>
      <input
        type="text"
        placeholder="Search by name or site ID…"
        value={search}
        onChange={e => setSearch(e.target.value)}
        className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm mb-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
      />
      <select
        id={id}
        value={value ?? ""}
        onChange={e => onChange(e.target.value === "" ? null : Number(e.target.value))}
        className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
        size={5}
      >
        <option value="">— select a site —</option>
        {filtered.map(s => (
          <option key={s.siteId} value={s.siteId}>
            [{s.siteId}] {s.companyName}
          </option>
        ))}
      </select>
    </div>
  );
}

// ── Conflict mode picker ───────────────────────────────────────────────────────

function ConflictModePicker({
  value,
  onChange
}: {
  value: ConflictMode;
  onChange: (v: ConflictMode) => void;
}) {
  const options: { value: ConflictMode; label: string; desc: string }[] = [
    { value: "skip", label: "Skip existing", desc: "Leave target items untouched if a match is found." },
    { value: "overwrite", label: "Overwrite", desc: "Replace target items with source versions." },
    { value: "rename", label: 'Rename with "(copy)" suffix', desc: "Create a copy alongside existing items." }
  ];
  return (
    <div>
      <div className="text-sm font-medium text-gray-700 mb-2">Conflict mode</div>
      <div className="space-y-2">
        {options.map(opt => (
          <label key={opt.value} className="flex items-start gap-2 cursor-pointer">
            <input
              type="radio"
              name="conflictMode"
              value={opt.value}
              checked={value === opt.value}
              onChange={() => onChange(opt.value)}
              className="mt-0.5"
            />
            <span>
              <span className="text-sm font-medium text-gray-800">{opt.label}</span>
              <span className="block text-xs text-gray-500">{opt.desc}</span>
            </span>
          </label>
        ))}
      </div>
    </div>
  );
}

// ── Include checkboxes ─────────────────────────────────────────────────────────

function IncludeCheckboxes({
  value,
  onChange
}: {
  value: IncludeOptions;
  onChange: (v: IncludeOptions) => void;
}) {
  const fields: { key: keyof IncludeOptions; label: string }[] = [
    { key: "workflows", label: "Workflows" },
    { key: "bots", label: "AI Bots" },
    { key: "siteSettings", label: "Site Settings" },
    { key: "deployments", label: "Deployments" }
  ];
  return (
    <div>
      <div className="text-sm font-medium text-gray-700 mb-2">Include</div>
      <div className="flex flex-wrap gap-4">
        {fields.map(f => (
          <label key={f.key} className="flex items-center gap-1.5 cursor-pointer text-sm text-gray-800">
            <input
              type="checkbox"
              checked={value[f.key]}
              onChange={e => onChange({ ...value, [f.key]: e.target.checked })}
            />
            {f.label}
          </label>
        ))}
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function SiteSync() {
  const api = useApi();
  const { displayToast } = useToastAlert();

  const [sites, setSites] = React.useState<SiteOption[]>([]);
  const [loadingSites, setLoadingSites] = React.useState(true);
  const [activeTab, setActiveTab] = React.useState<"clone" | "crossenv" | "blob">("clone");

  // Clone tab state
  const [cloneSource, setCloneSource] = React.useState<number | null>(null);
  const [cloneTarget, setCloneTarget] = React.useState<number | null>(null);
  const [cloneConflict, setCloneConflict] = React.useState<ConflictMode>("skip");
  const [cloneInclude, setCloneInclude] = React.useState<IncludeOptions>({
    workflows: true, bots: true, siteSettings: true, deployments: true
  });
  const [clonePreview, setClonePreview] = React.useState<SiteSyncResult | null>(null);
  const [clonePreviewing, setClonePreviewing] = React.useState(false);
  const [cloneApplying, setCloneApplying] = React.useState(false);
  const [clonePreviewDone, setClonePreviewDone] = React.useState(false);

  // Cross-env export state
  const [exportSource, setExportSource] = React.useState<number | null>(null);
  const [exporting, setExporting] = React.useState(false);

  // Cross-env import state
  const [importTarget, setImportTarget] = React.useState<number | null>(null);
  const [importConflict, setImportConflict] = React.useState<ConflictMode>("skip");
  const [importFile, setImportFile] = React.useState<File | null>(null);
  const [importPreview, setImportPreview] = React.useState<SiteSyncResult | null>(null);
  const [importPreviewing, setImportPreviewing] = React.useState(false);
  const [importApplying, setImportApplying] = React.useState(false);
  const [importPreviewDone, setImportPreviewDone] = React.useState(false);

  // Blob exchange state
  const [blobEntries, setBlobEntries] = React.useState<BlobEntry[]>([]);
  const [blobLoading, setBlobLoading] = React.useState(false);
  const [blobPushSource, setBlobPushSource] = React.useState<number | null>(null);
  const [blobPushLabel, setBlobPushLabel] = React.useState("");
  const [blobPushing, setBlobPushing] = React.useState(false);
  const [blobSelectedKey, setBlobSelectedKey] = React.useState<string | null>(null);
  const [blobImportTarget, setBlobImportTarget] = React.useState<number | null>(null);
  const [blobImportConflict, setBlobImportConflict] = React.useState<ConflictMode>("skip");
  const [blobImportPreview, setBlobImportPreview] = React.useState<SiteSyncResult | null>(null);
  const [blobImportPreviewing, setBlobImportPreviewing] = React.useState(false);
  const [blobImportApplying, setBlobImportApplying] = React.useState(false);
  const [blobImportPreviewDone, setBlobImportPreviewDone] = React.useState(false);

  React.useEffect(() => {
    (async () => {
      try {
        const data: SiteOption[] = await (await api.admin.get("SiteSync/sites")).json();
        setSites(Array.isArray(data) ? data : []);
      } catch (ex: any) {
        displayToast(Severity.Error, "Failed to load sites: " + ex.message);
        setSites([]);
      } finally {
        setLoadingSites(false);
      }
    })();
  }, []);

  // Reset preview when inputs change
  React.useEffect(() => {
    setClonePreview(null);
    setClonePreviewDone(false);
  }, [cloneSource, cloneTarget, cloneConflict, cloneInclude]);

  React.useEffect(() => {
    setImportPreview(null);
    setImportPreviewDone(false);
  }, [importTarget, importConflict, importFile]);

  React.useEffect(() => {
    setBlobImportPreview(null);
    setBlobImportPreviewDone(false);
  }, [blobSelectedKey, blobImportTarget, blobImportConflict]);

  // Load blob list when blob tab is opened
  React.useEffect(() => {
    if (activeTab === "blob") loadBlobList();
  }, [activeTab]);

  // ── Clone handlers ──

  async function handleClonePreview() {
    if (!cloneSource || !cloneTarget) return;
    setClonePreviewing(true);
    setClonePreview(null);
    try {
      const result: SiteSyncResult = await (
        await api.admin.get(
          `SiteSync/preview?sourceSiteId=${cloneSource}&targetSiteId=${cloneTarget}&conflictMode=${cloneConflict}`
        )
      ).json();
      setClonePreview(result);
      setClonePreviewDone(true);
    } catch (ex: any) {
      displayToast(Severity.Error, "Preview failed: " + ex.message);
    } finally {
      setClonePreviewing(false);
    }
  }

  async function handleCloneApply() {
    if (!cloneSource || !cloneTarget) return;
    setCloneApplying(true);
    try {
      const result: SiteSyncResult = await (
        await api.admin.post("SiteSync/apply", {
          sourceSiteId: cloneSource,
          targetSiteId: cloneTarget,
          conflictMode: cloneConflict,
          dryRun: false,
          include: {
            workflows: cloneInclude.workflows,
            bots: cloneInclude.bots,
            siteSettings: cloneInclude.siteSettings,
            deployments: cloneInclude.deployments
          }
        })
      ).json();
      setClonePreview(result);
      if (result.success) {
        displayToast(Severity.Success, "Site sync applied successfully.");
      } else {
        displayToast(Severity.Error, "Sync completed with errors — see details below.");
      }
    } catch (ex: any) {
      displayToast(Severity.Error, "Apply failed: " + ex.message);
    } finally {
      setCloneApplying(false);
    }
  }

  // ── Export handler ──

  async function handleExport() {
    if (!exportSource) return;
    setExporting(true);
    try {
      const resp = await api.admin.get(`SiteSync/export?sourceSiteId=${exportSource}`);
      if (!resp.ok) {
        const text = await resp.text();
        displayToast(Severity.Error, "Export failed: " + text);
        return;
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `site-${exportSource}-bundle.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (ex: any) {
      displayToast(Severity.Error, "Export failed: " + ex.message);
    } finally {
      setExporting(false);
    }
  }

  // ── Import handlers ──

  async function buildImportForm(dryRun: boolean): Promise<FormData> {
    const form = new FormData();
    form.append("targetSiteId", String(importTarget));
    form.append("conflictMode", importConflict);
    form.append("dryRun", String(dryRun));
    if (importFile) form.append("file", importFile);
    return form;
  }

  async function handleImportPreview() {
    if (!importTarget || !importFile) return;
    setImportPreviewing(true);
    setImportPreview(null);
    try {
      const form = await buildImportForm(true);
      const result: SiteSyncResult = await (
        await api.admin.postFormData("SiteSync/import", form)
      ).json();
      setImportPreview(result);
      setImportPreviewDone(true);
    } catch (ex: any) {
      displayToast(Severity.Error, "Preview failed: " + ex.message);
    } finally {
      setImportPreviewing(false);
    }
  }

  async function handleImportApply() {
    if (!importTarget || !importFile) return;
    setImportApplying(true);
    try {
      const form = await buildImportForm(false);
      const result: SiteSyncResult = await (
        await api.admin.postFormData("SiteSync/import", form)
      ).json();
      setImportPreview(result);
      if (result.success) {
        displayToast(Severity.Success, "Import applied successfully.");
      } else {
        displayToast(Severity.Error, "Import completed with errors — see details below.");
      }
    } catch (ex: any) {
      displayToast(Severity.Error, "Apply failed: " + ex.message);
    } finally {
      setImportApplying(false);
    }
  }

  // ── Blob handlers ──

  async function loadBlobList() {
    setBlobLoading(true);
    try {
      const data: BlobEntry[] = await (await api.admin.get("SiteSync/blob/list")).json();
      setBlobEntries(Array.isArray(data) ? data : []);
    } catch {
      setBlobEntries([]);
    } finally {
      setBlobLoading(false);
    }
  }

  async function handleBlobPush() {
    if (!blobPushSource) return;
    setBlobPushing(true);
    try {
      const resp: { success: boolean; blobKey?: string; error?: string } = await (
        await api.admin.post("SiteSync/blob/push", {
          sourceSiteId: blobPushSource,
          label: blobPushLabel || `site-${blobPushSource}`
        })
      ).json();

      if (resp.success) {
        displayToast(Severity.Success, "Bundle pushed to shared storage.");
        setBlobPushLabel("");
        await loadBlobList();
      } else {
        displayToast(Severity.Error, resp.error ?? "Push failed.");
      }
    } catch (ex: any) {
      displayToast(Severity.Error, "Push failed: " + ex.message);
    } finally {
      setBlobPushing(false);
    }
  }

  async function handleBlobImportPreview() {
    if (!blobSelectedKey || !blobImportTarget) return;
    setBlobImportPreviewing(true);
    setBlobImportPreview(null);
    try {
      const result: SiteSyncResult = await (
        await api.admin.post("SiteSync/blob/import", {
          blobKey: blobSelectedKey,
          targetSiteId: blobImportTarget,
          conflictMode: blobImportConflict,
          dryRun: true
        })
      ).json();
      setBlobImportPreview(result);
      setBlobImportPreviewDone(true);
    } catch (ex: any) {
      displayToast(Severity.Error, "Preview failed: " + ex.message);
    } finally {
      setBlobImportPreviewing(false);
    }
  }

  async function handleBlobImportApply() {
    if (!blobSelectedKey || !blobImportTarget) return;
    setBlobImportApplying(true);
    try {
      const result: SiteSyncResult = await (
        await api.admin.post("SiteSync/blob/import", {
          blobKey: blobSelectedKey,
          targetSiteId: blobImportTarget,
          conflictMode: blobImportConflict,
          dryRun: false
        })
      ).json();
      setBlobImportPreview(result);
      if (result.success) {
        displayToast(Severity.Success, "Bundle imported successfully.");
      } else {
        displayToast(Severity.Error, "Import completed with errors — see details below.");
      }
    } catch (ex: any) {
      displayToast(Severity.Error, "Apply failed: " + ex.message);
    } finally {
      setBlobImportApplying(false);
    }
  }

  async function handleBlobDelete(blobKey: string) {
    try {
      await api.admin.delete(`SiteSync/blob/${encodeURIComponent(blobKey)}`);
      setBlobEntries(prev => prev.filter(e => e.blobKey !== blobKey));
      if (blobSelectedKey === blobKey) setBlobSelectedKey(null);
      displayToast(Severity.Success, "Bundle deleted.");
    } catch (ex: any) {
      displayToast(Severity.Error, "Delete failed: " + ex.message);
    }
  }

  if (loadingSites) return <LoadingSpinner />;

  return (
    <div className="p-6 max-w-5xl mx-auto">

      {/* ── What won't be copied ── */}
      <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="font-semibold text-amber-800 text-sm mb-2">What is NOT copied — set these up on the target site separately</div>
        <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-xs text-amber-700">
          <span>• Agent / user accounts (must be invited on target)</span>
          <span>• OAuth tokens &amp; integration credentials (Shopify, HubSpot, etc.)</span>
          <span>• Phone numbers (SMS, WhatsApp, IVR)</span>
          <span>• Billing &amp; subscription settings</span>
          <span>• Conversations, messages &amp; transcripts</span>
          <span>• CSAT survey responses</span>
          <span>• Knowledge base articles &amp; AI index</span>
          <span>• Team members &amp; routing assignments</span>
        </div>
        <div className="mt-2 text-xs text-amber-600 font-medium">
          Workflows referencing integrations (e.g. Shopify skills) will copy but won't work until credentials are connected on the target.
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-gray-200">
        <TabButton active={activeTab === "clone"} onClick={() => setActiveTab("clone")}>
          Clone Site
        </TabButton>
        <TabButton active={activeTab === "crossenv"} onClick={() => setActiveTab("crossenv")}>
          Cross-Environment (file)
        </TabButton>
        <TabButton active={activeTab === "blob"} onClick={() => setActiveTab("blob")}>
          Shared Bundle Storage
        </TabButton>
      </div>

      {/* ── Clone Tab ── */}
      {activeTab === "clone" && (
        <div>
          <p className="text-sm text-gray-500 mb-6">
            Clone configuration from one site to another within this environment.
            Preview the plan before applying any changes.
          </p>

          <div className="grid grid-cols-2 gap-6 mb-6">
            <SiteSelect
              id="clone-source"
              sites={sites}
              value={cloneSource}
              exclude={cloneTarget}
              onChange={setCloneSource}
              label="Source site"
            />
            <SiteSelect
              id="clone-target"
              sites={sites}
              value={cloneTarget}
              exclude={cloneSource}
              onChange={setCloneTarget}
              label="Target site"
            />
          </div>

          <div className="grid grid-cols-2 gap-6 mb-6">
            <IncludeCheckboxes value={cloneInclude} onChange={setCloneInclude} />
            <ConflictModePicker value={cloneConflict} onChange={setCloneConflict} />
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleClonePreview}
              disabled={!cloneSource || !cloneTarget || clonePreviewing}
              className="px-4 py-2 bg-white border border-gray-300 rounded text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {clonePreviewing ? "Previewing…" : "Preview"}
            </button>
            <button
              onClick={handleCloneApply}
              disabled={!clonePreviewDone || cloneApplying || clonePreviewing}
              className="px-4 py-2 bg-blue-600 text-white rounded text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {cloneApplying ? "Applying…" : "Apply"}
            </button>
          </div>

          {clonePreview && <SyncPlanTable result={clonePreview} />}
        </div>
      )}

      {/* ── Cross-Environment Tab (file download/upload) ── */}
      {activeTab === "crossenv" && (
        <div>
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded text-sm text-blue-800">
            Export a bundle JSON file from one environment, then upload it on the target environment.
            For a seamless no-file workflow, use the <strong>Shared Bundle Storage</strong> tab instead.
          </div>

          {/* Export section */}
          <div className="mb-8">
            <h2 className="text-base font-semibold text-gray-800 mb-3">Export</h2>
            <div className="grid grid-cols-2 gap-6 mb-4">
              <SiteSelect
                id="export-source"
                sites={sites}
                value={exportSource}
                exclude={null}
                onChange={setExportSource}
                label="Source site"
              />
            </div>
            <button
              onClick={handleExport}
              disabled={!exportSource || exporting}
              className="px-4 py-2 bg-white border border-gray-300 rounded text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {exporting ? "Downloading…" : "Download Bundle"}
            </button>
          </div>

          <hr className="border-gray-200 mb-8" />

          {/* Import section */}
          <div>
            <h2 className="text-base font-semibold text-gray-800 mb-3">Import</h2>
            <div className="grid grid-cols-2 gap-6 mb-6">
              <SiteSelect
                id="import-target"
                sites={sites}
                value={importTarget}
                exclude={null}
                onChange={setImportTarget}
                label="Target site"
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Bundle file (.json)
                </label>
                <div
                  className="border-2 border-dashed border-gray-300 rounded p-4 text-center cursor-pointer hover:border-blue-400 transition-colors"
                  onDragOver={e => e.preventDefault()}
                  onDrop={e => {
                    e.preventDefault();
                    const file = e.dataTransfer.files[0];
                    if (file) setImportFile(file);
                  }}
                  onClick={() => document.getElementById("import-file-input")?.click()}
                >
                  {importFile ? (
                    <div className="text-sm text-gray-800">
                      <span className="font-medium">{importFile.name}</span>
                      <span className="ml-2 text-gray-500">
                        ({(importFile.size / 1024).toFixed(1)} KB)
                      </span>
                      <button
                        className="ml-3 text-red-500 text-xs hover:underline"
                        onClick={e => { e.stopPropagation(); setImportFile(null); }}
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500">
                      Drop a .json bundle here or <span className="text-blue-600 underline">click to browse</span>
                    </div>
                  )}
                  <input
                    id="import-file-input"
                    type="file"
                    accept=".json"
                    className="hidden"
                    onChange={e => {
                      const file = e.target.files?.[0];
                      if (file) setImportFile(file);
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="mb-6">
              <ConflictModePicker value={importConflict} onChange={setImportConflict} />
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleImportPreview}
                disabled={!importTarget || !importFile || importPreviewing}
                className="px-4 py-2 bg-white border border-gray-300 rounded text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {importPreviewing ? "Previewing…" : "Preview Import"}
              </button>
              <button
                onClick={handleImportApply}
                disabled={!importPreviewDone || importApplying || importPreviewing}
                className="px-4 py-2 bg-blue-600 text-white rounded text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {importApplying ? "Applying…" : "Apply Import"}
              </button>
            </div>

            {importPreview && <SyncPlanTable result={importPreview} />}
          </div>
        </div>
      )}

      {/* ── Blob Exchange Tab ── */}
      {activeTab === "blob" && (
        <div>
          <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded text-sm text-emerald-800">
            Push a bundle to shared Azure Blob Storage — accessible from any environment (staging or production).
            No file download needed. Pick a bundle from the list below and apply it directly to any target site.
          </div>

          {/* Push section */}
          <div className="mb-8 p-4 border border-gray-200 rounded-lg">
            <h2 className="text-base font-semibold text-gray-800 mb-4">Push Bundle to Shared Storage</h2>
            <div className="grid grid-cols-2 gap-6 mb-4">
              <SiteSelect
                id="blob-push-source"
                sites={sites}
                value={blobPushSource}
                exclude={null}
                onChange={setBlobPushSource}
                label="Source site"
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Bundle label <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  value={blobPushLabel}
                  onChange={e => setBlobPushLabel(e.target.value)}
                  placeholder={`e.g. staging-baseline-${new Date().toISOString().slice(0, 7)}`}
                  className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-400 mt-1">Used to identify the bundle in the list below.</p>
              </div>
            </div>
            <button
              onClick={handleBlobPush}
              disabled={!blobPushSource || blobPushing}
              className="px-4 py-2 bg-emerald-600 text-white rounded text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {blobPushing ? "Pushing…" : "Push to Shared Storage"}
            </button>
          </div>

          {/* Bundle list */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base font-semibold text-gray-800">Available Bundles</h2>
              <button
                onClick={loadBlobList}
                disabled={blobLoading}
                className="text-xs px-3 py-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40"
              >
                {blobLoading ? "Loading…" : "Refresh"}
              </button>
            </div>

            {blobEntries.length === 0 ? (
              <div className="text-sm text-gray-400 border border-gray-200 rounded p-6 text-center">
                {blobLoading ? "Loading…" : "No bundles in shared storage yet. Push one above."}
              </div>
            ) : (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                    <tr>
                      <th className="px-3 py-2 text-left w-6"></th>
                      <th className="px-3 py-2 text-left">Label</th>
                      <th className="px-3 py-2 text-left w-20">Site</th>
                      <th className="px-3 py-2 text-left w-36">Pushed</th>
                      <th className="px-3 py-2 text-left w-20">Size</th>
                      <th className="px-3 py-2 text-left w-32">By</th>
                      <th className="px-3 py-2 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {blobEntries.map(entry => (
                      <tr
                        key={entry.blobKey}
                        onClick={() => setBlobSelectedKey(entry.blobKey === blobSelectedKey ? null : entry.blobKey)}
                        className={`border-t border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors ${
                          blobSelectedKey === entry.blobKey ? "bg-blue-50 ring-inset ring-1 ring-blue-400" : ""
                        }`}
                      >
                        <td className="px-3 py-2">
                          <input
                            type="radio"
                            readOnly
                            checked={blobSelectedKey === entry.blobKey}
                            className="pointer-events-none"
                          />
                        </td>
                        <td className="px-3 py-2 font-medium text-gray-800">{entry.label}</td>
                        <td className="px-3 py-2 text-gray-500">{entry.sourceSiteId}</td>
                        <td className="px-3 py-2 text-gray-500 text-xs">
                          {new Date(entry.exportedAt).toLocaleString()}
                        </td>
                        <td className="px-3 py-2 text-gray-400 text-xs">
                          {(entry.sizeBytes / 1024).toFixed(1)} KB
                        </td>
                        <td className="px-3 py-2 text-gray-400 text-xs truncate max-w-[8rem]">
                          {entry.pushedBy ?? "—"}
                        </td>
                        <td className="px-3 py-2">
                          <button
                            onClick={e => { e.stopPropagation(); handleBlobDelete(entry.blobKey); }}
                            className="text-red-400 hover:text-red-600 text-xs"
                            title="Delete bundle"
                          >
                            ✕
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Import from selected bundle */}
          {blobSelectedKey && (
            <div className="p-4 border border-blue-200 bg-blue-50 rounded-lg">
              <h2 className="text-base font-semibold text-gray-800 mb-4">
                Apply Bundle to Target Site
              </h2>
              <p className="text-xs text-gray-500 mb-4 font-mono break-all">{blobSelectedKey}</p>

              <div className="grid grid-cols-2 gap-6 mb-4">
                <SiteSelect
                  id="blob-import-target"
                  sites={sites}
                  value={blobImportTarget}
                  exclude={null}
                  onChange={setBlobImportTarget}
                  label="Target site"
                />
                <ConflictModePicker value={blobImportConflict} onChange={setBlobImportConflict} />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleBlobImportPreview}
                  disabled={!blobImportTarget || blobImportPreviewing}
                  className="px-4 py-2 bg-white border border-gray-300 rounded text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {blobImportPreviewing ? "Previewing…" : "Preview"}
                </button>
                <button
                  onClick={handleBlobImportApply}
                  disabled={!blobImportPreviewDone || blobImportApplying || blobImportPreviewing}
                  className="px-4 py-2 bg-blue-600 text-white rounded text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {blobImportApplying ? "Applying…" : "Apply to Site"}
                </button>
              </div>

              {blobImportPreview && <SyncPlanTable result={blobImportPreview} />}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
